import * as React from "react";
import { cx } from "../internal/cx.js";
import { Label, type LabelSize } from "./label.js";

export type TextAreaSize = "sm" | "md" | "lg";

export interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: string;
  helperText?: string;
  label?: React.ReactNode;
  optional?: boolean;
  size?: TextAreaSize;
}

function getLabelSize(size: TextAreaSize): LabelSize {
  return size === "sm" ? "sm" : size === "lg" ? "lg" : "md";
}

function hasValue(
  value:
    | React.TextareaHTMLAttributes<HTMLTextAreaElement>["value"]
    | React.TextareaHTMLAttributes<HTMLTextAreaElement>["defaultValue"],
): boolean {
  return value !== undefined && value !== null && String(value).length > 0;
}

/**
 * Multi-line text input. Owns the label/helper/error region like
 * `<Input>` so a single textarea field is consistent with single-line
 * inputs in the same form.
 */
export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  {
    className,
    defaultValue,
    disabled,
    error,
    helperText,
    id,
    label,
    optional = false,
    readOnly,
    size = "md",
    value,
    ...props
  },
  ref,
) {
  const generatedId = React.useId();
  const textAreaId = id ?? generatedId;
  const helperId = helperText && !error ? `${textAreaId}-helper` : undefined;
  const errorId = error ? `${textAreaId}-error` : undefined;
  const filled = hasValue(value) || hasValue(defaultValue);

  return (
    <div className="cw-v2-input-field">
      {label ? (
        <Label htmlFor={textAreaId} optional={optional} size={getLabelSize(size)}>
          {label}
        </Label>
      ) : null}
      <textarea
        ref={ref}
        aria-describedby={errorId ?? helperId}
        aria-invalid={error ? true : undefined}
        className={cx(
          "cw-v2-textarea",
          `cw-v2-textarea--${size}`,
          error && "cw-v2-textarea--error",
          disabled && "cw-v2-textarea--disabled",
          filled && "cw-v2-textarea--filled",
          readOnly && "cw-v2-textarea--readonly",
          className,
        )}
        defaultValue={defaultValue}
        disabled={disabled}
        id={textAreaId}
        readOnly={readOnly}
        value={value}
        {...props}
      />
      {error ? (
        <p className="cw-v2-input__message cw-v2-input__message--error" id={errorId}>
          {error}
        </p>
      ) : helperText ? (
        <p className="cw-v2-input__message" id={helperId}>
          {helperText}
        </p>
      ) : null}
    </div>
  );
});
TextArea.displayName = "TextArea";
